'use client'

import Link from 'next/link'
import type { MouseEvent, ReactNode } from 'react'
import { cx } from '@/app/lib/utils'
import styles from './Button.module.scss'

export type ButtonVariant =
  | 'filled'
  | 'filled-subtle'
  | 'outlined'
  | 'ghost'
  | 'filled-inverse'
  | 'filled-accent'
export type ButtonShape = 'block' | 'rounded'
export type ButtonSize = 'l' | 'm' | 's' | 'xs'

interface ButtonBaseProps {
  children: ReactNode
  variant?: ButtonVariant
  shape?: ButtonShape
  size?: ButtonSize
  /** Ocupa el 100% del ancho y centra el contenido. */
  stretch?: boolean
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
  /** Etiqueta accesible. Obligatoria si el contenido visible no es texto. */
  ariaLabel?: string
  /** Clases extra para ajustes de layout del contenedor (p. ej. ancho). */
  className?: string
}

/** Acción (`<button>`): admite estados de `disabled`/`loading` y `onClick`. */
interface ButtonAsButton extends ButtonBaseProps {
  href?: undefined
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  loading?: boolean
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void
}

/** Navegación (`<a>` vía next/link): sin estados de acción. */
interface ButtonAsLink extends ButtonBaseProps {
  href: string
  /**
   * `true` ⇒ abre el destino en una pestaña nueva de forma segura
   * (`target="_blank"` + `rel="noopener noreferrer"`), en vez de navegar con
   * `next/link`. Por defecto `false`. Mismo criterio que el átomo `Link`
   * (`LinkAsExternal`).
   */
  external?: boolean
  /**
   * Handler opcional para instrumentación (analítica u otro side-effect) antes
   * de navegar — nunca bloquea ni retrasa la navegación (no hay
   * `preventDefault` en ninguna rama). Ej.: `PurchaseButton` lo usa para
   * empujar `click`/`add_to_cart` antes de redirigir al vendor externo.
   */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void
}

export type ButtonProps = ButtonAsButton | ButtonAsLink

export function Button(props: ButtonProps) {
  const {
    children,
    variant = 'filled',
    shape = 'block',
    size = 'm',
    stretch = false,
    leadingIcon,
    trailingIcon,
    ariaLabel,
    className,
  } = props

  const rootClass = cx(
    styles.btn,
    styles[`btn--${variant}`],
    styles[`btn--${shape}`],
    styles[`btn--${size}`],
    stretch && styles['btn--stretch'],
    className,
  )

  const inner = (loading: boolean): ReactNode => (
    <>
      {loading && <span className={styles.spinner} aria-hidden="true" />}
      {!loading && leadingIcon && (
        <span className={styles.icon} aria-hidden="true">
          {leadingIcon}
        </span>
      )}
      <span className={styles.label}>{children}</span>
      {!loading && trailingIcon && (
        <span className={styles.icon} aria-hidden="true">
          {trailingIcon}
        </span>
      )}
    </>
  )

  // Variante navegación: enlace estilizado como botón.
  if (props.href !== undefined) {
    // Pestaña nueva segura: `<a>` normal, no `next/link` (mismo criterio que
    // el átomo `Link` en su rama `external`).
    if (props.external) {
      return (
        <a
          href={props.href}
          target="_blank"
          rel="noopener noreferrer"
          className={rootClass}
          aria-label={ariaLabel}
          onClick={props.onClick}
        >
          {inner(false)}
        </a>
      )
    }

    return (
      <Link href={props.href} className={rootClass} aria-label={ariaLabel} onClick={props.onClick}>
        {inner(false)}
      </Link>
    )
  }

  // Variante acción.
  const { type = 'button', disabled = false, loading = false, onClick } = props
  return (
    <button
      type={type}
      className={cx(rootClass, loading && styles['btn--loading'])}
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
    >
      {inner(loading)}
    </button>
  )
}
